var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCustomerData } from './useCustomerData';
import { useStorage } from '../contexts';
import { GeneralActions, customerConfigVersionSelector } from '../redux';
import { decodeCustomerConfig } from '../utils/config.utils';
import { StorageKeys } from '../services';
/**
 * Hook per sincronizzare il config del customer (JWT) con Storage e Redux.
 * Usa Storage come fonte di verità per la versione del config.
 * Redux è solo una cache in-memory.
 */
export var useCustomerConfigSync = function () {
    var _a;
    var customer = useCustomerData().customer;
    var dispatch = useDispatch();
    var storage = useStorage();
    var currentConfigVersion = useSelector(customerConfigVersionSelector);
    // Inizializza Redux da Storage all'avvio
    useEffect(function () {
        if (currentConfigVersion !== null) {
            return; // Redux già inizializzato
        }
        var initializeFromStorage = function () { return __awaiter(void 0, void 0, void 0, function () {
            var storedVersion, storedConfigStr, storedConfig;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, storage.getItem(StorageKeys.CUSTOMER_CONFIG_VERSION)];
                    case 1:
                        storedVersion = _a.sent();
                        return [4 /*yield*/, storage.getItem(StorageKeys.CUSTOMER_CONFIG)];
                    case 2:
                        storedConfigStr = _a.sent();
                        if (storedVersion && storedConfigStr) {
                            try {
                                storedConfig = JSON.parse(storedConfigStr);
                                /*console.log('📥 Restoring config from Storage', storedVersion);
                                console.log(
                                  '📋 Complete decrypted config from Storage:',
                                  JSON.stringify(storedConfig, null, 2),
                                );*/
                                dispatch(GeneralActions.setCustomerConfig({
                                    config: storedConfig,
                                    version: storedVersion,
                                }));
                            }
                            catch (error) {
                                // Failed to parse stored config
                            }
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        initializeFromStorage();
    }, [currentConfigVersion, dispatch, storage]);
    // Sincronizza il config dal JWT con Storage (se la versione cambia)
    useEffect(function () {
        var syncConfig = function () { return __awaiter(void 0, void 0, void 0, function () {
            var accessToken, decodedConfig, newVersion, storedVersion;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(!customer || !((_a = customer.config) === null || _a === void 0 ? void 0 : _a.data))) return [3 /*break*/, 4];
                        return [4 /*yield*/, storage.getItem(StorageKeys.ACCESS_TOKEN_KEY)];
                    case 1:
                        accessToken = _b.sent();
                        // Se c'è un token, NON pulire i dati (errore temporaneo, il refresh token gestirà)
                        // La pulizia viene gestita in useLogout quando l'utente fa logout esplicito
                        if (accessToken) {
                            return [2 /*return*/];
                        }
                        // Se non c'è token, l'utente non è autenticato, quindi pulisci tutto
                        return [4 /*yield*/, storage.removeItem(StorageKeys.CUSTOMER_CONFIG)];
                    case 2:
                        // Se non c'è token, l'utente non è autenticato, quindi pulisci tutto
                        _b.sent();
                        return [4 /*yield*/, storage.removeItem(StorageKeys.CUSTOMER_CONFIG_VERSION)];
                    case 3:
                        _b.sent();
                        if (currentConfigVersion !== null) {
                            // Clearing customer config (no customer or config and no token)
                            dispatch(GeneralActions.clearCustomerConfig());
                        }
                        return [2 /*return*/];
                    case 4:
                        decodedConfig = decodeCustomerConfig(customer.config.data);
                        if (!!decodedConfig) return [3 /*break*/, 7];
                        // Failed to decode customer config JWT
                        return [4 /*yield*/, storage.removeItem(StorageKeys.CUSTOMER_CONFIG)];
                    case 5:
                        // Failed to decode customer config JWT
                        _b.sent();
                        return [4 /*yield*/, storage.removeItem(StorageKeys.CUSTOMER_CONFIG_VERSION)];
                    case 6:
                        _b.sent();
                        if (currentConfigVersion !== null) {
                            dispatch(GeneralActions.clearCustomerConfig());
                        }
                        return [2 /*return*/];
                    case 7:
                        newVersion = decodedConfig.version || '0.0.0';
                        return [4 /*yield*/, storage.getItem(StorageKeys.CUSTOMER_CONFIG_VERSION)];
                    case 8:
                        storedVersion = _b.sent();
                        if (!(storedVersion !== newVersion)) return [3 /*break*/, 11];
                        // Updating customer config in Storage
                        // Salva in Storage
                        return [4 /*yield*/, storage.setItem(StorageKeys.CUSTOMER_CONFIG_VERSION, newVersion)];
                    case 9:
                        // Updating customer config in Storage
                        // Salva in Storage
                        _b.sent();
                        return [4 /*yield*/, storage.setItem(StorageKeys.CUSTOMER_CONFIG, JSON.stringify(decodedConfig))];
                    case 10:
                        _b.sent();
                        // Aggiorna Redux
                        dispatch(GeneralActions.setCustomerConfig({
                            config: decodedConfig,
                            version: newVersion,
                        }));
                        return [3 /*break*/, 11];
                    case 11: return [2 /*return*/];
                }
            });
        }); };
        syncConfig();
    }, [(_a = customer === null || customer === void 0 ? void 0 : customer.config) === null || _a === void 0 ? void 0 : _a.data, currentConfigVersion, dispatch, storage]);
};
